import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Plus, RefreshCw } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/page-header';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { findPropertyByCode, fetchOrgProperties, fetchUserProperties } from '@/features/properties/propertiesThunks';
import { normalizePropertyCode, PROPERTY_CODE_PATTERN } from '@/features/properties/propertyCode';
import type { IProperty, PropertyStatus } from '@/domain/entities/property';
import { selectActiveOrganizationId } from '@/store/selectors/authorizationSelectors';
import { usePermissions } from '@/hooks/usePermissions';
import { Permissions } from '@/lib/permissions';
import {
  fetchHomeownerProperties,
  selectHasFetchedHomeownerPropertiesByUserId,
  selectHomeownerPropertiesByUserId,
  selectHomeownerPropertiesErrorByUserId,
  selectHomeownerPropertiesLoadingByUserId,
} from '@/features/homeowners';

function getStatusVariant(status: PropertyStatus | 'Unknown') {
  switch (status) {
    case 'builderDraft':
      return 'default';
    case 'readyForHandover':
      return 'secondary';
    case 'owned':
      return 'outline';
    default:
      return 'default';
  }
}

// You can later replace this with a real field on IProperty (if you have one)

function inferStatus(_p: IProperty): PropertyStatus | 'Unknown' {
  return _p.status ?? 'Unknown';
}

// best-effort "display owner"
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function inferOwner(_p: IProperty): string {
  return '—';
}

const PropertyListPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { hasPermission, isSuperAdmin } = usePermissions();
  const narrowedApplicationUserId = isSuperAdmin ? (searchParams.get('applicationUserId')?.trim() ?? '') : '';

  // orgID (builder uses org scope)
  const orgID = useAppSelector(selectActiveOrganizationId);

  // store data
  const allProperties = useAppSelector((s) => s.properties.items);
  const isLoadingAllProperties = useAppSelector((s) => s.properties.isListing);
  const allPropertiesError = useAppSelector((s) => s.properties.errorListing);
  const hasFetchedAllProperties = useAppSelector((s) => s.properties.hasFetched);
  const lastFetchedAt = useAppSelector((s) => s.properties.lastFetchedAt);
  const claimedProperties = useAppSelector(selectHomeownerPropertiesByUserId(narrowedApplicationUserId));
  const isLoadingClaimedProperties = useAppSelector(
    selectHomeownerPropertiesLoadingByUserId(narrowedApplicationUserId),
  );
  const claimedPropertiesError = useAppSelector(selectHomeownerPropertiesErrorByUserId(narrowedApplicationUserId));
  const hasFetchedClaimedProperties = useAppSelector(
    selectHasFetchedHomeownerPropertiesByUserId(narrowedApplicationUserId),
  );

  const properties = narrowedApplicationUserId ? claimedProperties : allProperties;
  const isLoading = narrowedApplicationUserId ? isLoadingClaimedProperties : isLoadingAllProperties;
  const error = narrowedApplicationUserId ? claimedPropertiesError : allPropertiesError;
  const hasFetched = narrowedApplicationUserId ? hasFetchedClaimedProperties : hasFetchedAllProperties;

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [propertyCode, setPropertyCode] = useState('');
  const [propertyCodeError, setPropertyCodeError] = useState<string | null>(null);
  const [isLookingUpProperty, setIsLookingUpProperty] = useState(false);

  const canLoadProperties = isSuperAdmin || Boolean(orgID);
  const canLookupPropertyByCode = hasPermission(Permissions.PropertiesRead);

  // fetch on mount / org change
  useEffect(() => {
    if (!canLoadProperties) return;
    if (hasFetched) return;

    if (narrowedApplicationUserId) {
      void dispatch(fetchHomeownerProperties({ userID: narrowedApplicationUserId }));
      return;
    }

    if (isSuperAdmin) {
      void dispatch(fetchUserProperties());
      return;
    }

    if (!orgID) return;
    void dispatch(fetchOrgProperties({ orgID }));
  }, [canLoadProperties, dispatch, hasFetched, isSuperAdmin, narrowedApplicationUserId, orgID]);

  // derive available filter values from live data
  const propertyStatuses = useMemo(() => {
    const set = new Set<string>();
    for (const p of properties) set.add(inferStatus(p));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [properties]);

  const filteredProperties = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return properties.filter((p) => {
      const addressText = typeof p.address === 'string' ? p.address : (p.address?.address ?? '');
      const id = (p.id ?? '').toLowerCase();

      const matchesSearch =
        q === '' ||
        addressText.toLowerCase().includes(q) ||
        (p.title ?? '').toLowerCase().includes(q) ||
        id.toLowerCase().includes(q);

      const status = inferStatus(p);

      const matchesStatus = statusFilter === 'all' || status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [properties, searchQuery, statusFilter]);

  const onRefresh = () => {
    if (!canLoadProperties) return;

    if (narrowedApplicationUserId) {
      void dispatch(fetchHomeownerProperties({ userID: narrowedApplicationUserId, force: true }));
      return;
    }

    if (isSuperAdmin) {
      void dispatch(fetchUserProperties());
      return;
    }

    if (!orgID) return;
    void dispatch(fetchOrgProperties({ orgID }));
  };

  const handlePropertyCodeLookup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedCode = normalizePropertyCode(propertyCode);
    setPropertyCode(normalizedCode);

    if (!PROPERTY_CODE_PATTERN.test(normalizedCode)) {
      setPropertyCodeError('Enter a valid six-character property code');
      return;
    }

    setIsLookingUpProperty(true);
    setPropertyCodeError(null);

    try {
      const property = await dispatch(findPropertyByCode(normalizedCode)).unwrap();
      navigate(`/properties/${encodeURIComponent(property.id)}/edit`, {
        state: { fromPropertiesList: true },
      });
    } catch (error) {
      const message =
        typeof error === 'object' && error && 'message' in error && typeof error.message === 'string'
          ? error.message
          : 'Property lookup failed';
      setPropertyCodeError(message);
    } finally {
      setIsLookingUpProperty(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={narrowedApplicationUserId ? 'Claimed Property Memberships' : 'Property List'}
        description={
          narrowedApplicationUserId
            ? 'Properties returned by the authoritative homeowner-membership lookup for the selected application user.'
            : undefined
        }
        right={
          <div className="flex items-center gap-2">
            {lastFetchedAt && !narrowedApplicationUserId ? (
              <span className="text-muted-foreground text-xs">
                Updated {new Date(lastFetchedAt).toLocaleTimeString()}
              </span>
            ) : null}

            <Button variant="outline" size="sm" onClick={onRefresh} disabled={!canLoadProperties || isLoading}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        }
      />

      {narrowedApplicationUserId ? (
        <div className="flex flex-col gap-3 rounded-lg border border-sky-500/40 bg-sky-500/5 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-medium">Claimed membership filter</p>
            <p className="text-muted-foreground mt-1 font-mono text-xs break-all">{narrowedApplicationUserId}</p>
          </div>
          <Button type="button" variant="outline" onClick={() => navigate('/properties')}>
            Clear filter
          </Button>
        </div>
      ) : null}

      {canLookupPropertyByCode && !narrowedApplicationUserId ? (
        <form
          onSubmit={handlePropertyCodeLookup}
          className="border-border bg-muted/20 flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-end"
        >
          <div className="flex-1 space-y-2">
            <label htmlFor="property-code" className="text-sm font-medium">
              Property code
            </label>
            <Input
              id="property-code"
              value={propertyCode}
              onChange={(event) => {
                setPropertyCode(event.target.value.toUpperCase());
                if (propertyCodeError) setPropertyCodeError(null);
              }}
              placeholder="NK6F3P"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              aria-invalid={Boolean(propertyCodeError)}
              aria-describedby={propertyCodeError ? 'property-code-error' : undefined}
              className="font-mono text-lg font-bold tracking-[0.15em] uppercase"
            />
            {propertyCodeError ? (
              <p id="property-code-error" className="text-destructive text-sm" role="alert">
                {propertyCodeError}
              </p>
            ) : (
              <p className="text-muted-foreground text-xs">Enter the six-character code shown on the property.</p>
            )}
          </div>
          <Button type="submit" disabled={isLookingUpProperty || !propertyCode.trim()}>
            <Search className="mr-2 h-4 w-4" />
            {isLookingUpProperty ? 'Looking up…' : 'Find property'}
          </Button>
        </form>
      ) : null}

      {/* Search and Filters */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Search by address, owner, id..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-[200px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {propertyStatuses.map((status) => (
              <SelectItem key={status} value={status}>
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Errors / Loading */}
      {!canLoadProperties ? (
        <div className="text-muted-foreground rounded-md border p-4">No organization found for the current user.</div>
      ) : error ? (
        <div className="text-destructive rounded-md border p-4">Failed to load properties: {error}</div>
      ) : null}

      {/* Table */}
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-semibold">Property ID</TableHead>
              <TableHead className="font-semibold">Title</TableHead>
              <TableHead className="font-semibold">Current Status</TableHead>
              <TableHead className="font-semibold">Owner</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24">
                  <div className="flex justify-center">
                    <LoadingSpinner />
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredProperties.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  No properties found.
                </TableCell>
              </TableRow>
            ) : (
              filteredProperties.map((p) => {
                const status = inferStatus(p);
                const owner = inferOwner(p);

                return (
                  <TableRow
                    key={p.id}
                    className="cursor-pointer"
                    onClick={() =>
                      navigate(`/properties/${encodeURIComponent(p.id)}/edit`, {
                        state: { fromPropertiesList: true },
                      })
                    }
                  >
                    <TableCell className="font-medium">{p.id}</TableCell>
                    <TableCell className="text-muted-foreground">{p.title ?? '—'}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(status)}>{status}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{owner}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Floating Add Button */}
      <div className="fixed right-8 bottom-8">
        <Button size="lg" className="shadow-lg" onClick={() => navigate('/properties/add')}>
          <Plus className="mr-2 h-5 w-5" />
          Add Property
        </Button>
      </div>
    </div>
  );
};

export default PropertyListPage;
